import React, {useState, useMemo, useCallback} from "react";

import {A11y, Secondary} from "@churchofjesuschrist/eden-buttons";
import { Menu, Clear, Text } from "@churchofjesuschrist/eden-icons";
import { backgroundColorAlt,
  backgroundColorLevelCurrent,
  spacing8 as buttonSpacing,
  spacing16 as buttonPadding,
  backgroundColorAlt as dividerGrey,
  accentColorPrimary as blue,
  green40 as green,
  textColorSecondary as grey,
  red40 as red } from "@churchofjesuschrist/eden-style-constants";
import { Link } from "react-router-dom";
import styled from "styled-components";

import HelpButton from "../../../generic/buttons/help-button/help-button";
import useToggle from "../../../generic/hooks/use-toggle/use-toggle";
import { WorldSearch } from "../../../generic/icons/svg-icons/svg-icons";
import HelpArticle from "../../../generic/navigation/top-navigation/help-article";
import stringHasValue from "../../../utils/string-has-value";
import useManagedStrings from "../../../utils/use-managed-strings";
import AccordionNavigation from "../../accordion-navigation/accordion-navigation";
import ProgramNumberSearch from "../../program-number-search/program-number-search";
import ProgramTreeNavigation from "../../program-tree-navigation/program-tree-navigation";


const MenuContainer = styled.div`
  position:absolute;
  top:0;
  right:0;
  width:100%;
  height:100%;
  z-index:10000;
  background-color:${backgroundColorLevelCurrent};
`;
const BarContainer = styled.div`
  height: 45px;
  display: flex;
  align-content: stretch;
  color: white;
`;
const Button = styled(A11y)`
  background-color:${(props) => props.active ? backgroundColorLevelCurrent : "none"};
  flex: 0 0 1.5rem;
  padding: ${buttonSpacing} ${buttonPadding};
  margin: 0 ${buttonSpacing} 0 0;
  font-size: ${(props) => props.isText ? '1em' : '1.5em'};
  cursor: pointer;
  color: ${(props) => props.color};
  height:100%;
  border-radius: 7px 7px 0 0;
  span {
    display: flex;
    justify-content: center;
    align-items: center;
    svg {
      margin-right: 6px;
    }
  }
`;
const Filler = styled.div`
  flex:1;
`;

const BarContents = styled.div`
  background-color:${backgroundColorAlt};
  display: flex;
  align-self: stretch;
  flex: 1;
`;
const ActionsContents = styled.div`
  padding:0 ${buttonPadding};
`;

const HelpCenterHolder = styled.div`
  margin-top:${buttonPadding};
  display:inline-block;
`
const HelpButtonHolder = styled.div`
  border-bottom: solid 1px ${dividerGrey};
  padding: 0 ${buttonPadding};
  display:flex;
  button {
      flex:1;
      padding: ${buttonPadding} 0;
  }
`

const HelpHolder = styled.div`
position:absolute;
background-color:${backgroundColorLevelCurrent};
right:0;
top:45px;
width:100%;
height:calc(100vh - 47px);
padding:0 ${buttonPadding} ${buttonPadding} ${buttonPadding};
`;
const HelpHeader = styled.div`
display:flex;
flex-direction:row-reverse;
`;

const CloseHelpButton = styled(A11y)`
padding: ${buttonPadding};
`
const stringGroups = [
  {
    name:"shared",
    keys:["helpCenter", "program", "actions"]
  }
];

const defaultFunc = () => {};
const MobileTopMenu = ({onClose = defaultFunc,
  helpArticle,
  theme,
  programTreeProps = {},
  programSearchProps = {},
  children}) => {

  const color = useMemo(() => {
    let match = grey;
    switch (theme) {
    case "institute":
      match = blue;
      break;
    case "seminary":
      match = green;
      break;
    case "area":
      match = red;
      break;
    default:
      match = grey;
      break;
    }
    return match;
  },[theme]);
  const {managedStrings,strings} = useManagedStrings(stringGroups);

  const [activeTab,setActiveTab] = useState("hamburger");
  const [helpOpen,toggleHelp] = useToggle(false);
  const hasChildren = children !== undefined;
  const hasArticle = stringHasValue(helpArticle);
  const showActions = hasChildren || hasArticle;

  const onTabChosen = useCallback((tab) => {
    setActiveTab(tab);
    if (helpOpen) {
      toggleHelp();
    }
  },[helpOpen, toggleHelp]);

  return <MenuContainer>
    <BarContainer data-id="mobile-top-menu">
      <BarContents>
        <Button color={color} data-id="hamburger"
          active={activeTab === "hamburger"}
          onClick={() => onTabChosen("hamburger")}>
          <Menu/>
        </Button>
        {showActions && <Button color={color} isText={true} data-id="actions"
          active={activeTab === "actions"}
          onClick={() => onTabChosen("actions")}>
          <span>
            <Text size="1.5rem" />
            {managedStrings.actions}
          </span>
        </Button>}
        <Button color={color} isText={true} data-id="navigation"
          active={activeTab === "navigation"}
          onClick={() => onTabChosen("navigation")}>
          <span>
            <WorldSearch fill={color} />
            {managedStrings.program}
          </span>
        </Button>
        <Filler/>
        <Button color={color} data-id="close"
          onClick={onClose}>
          <Clear/>
        </Button>
      </BarContents>
    </BarContainer>
    {activeTab === "hamburger" && (<AccordionNavigation/>)}
    {activeTab === "actions" && <ActionsContents>
      <div>{children}</div>
      {hasArticle && <HelpButtonHolder><HelpButton onClick={toggleHelp}/></HelpButtonHolder>}
      {helpOpen && <HelpHolder>
        <HelpHeader>
          <CloseHelpButton onClick={toggleHelp}><Clear size="20px"/></CloseHelpButton>
          <Filler/>
          <HelpCenterHolder>
            <Link data-id="page-help-help-center" to="/help-center">
              <Secondary>{managedStrings.helpCenter}</Secondary>
            </Link>
          </HelpCenterHolder>
        </HelpHeader>
        <HelpArticle helpArticle={helpArticle} isMobile={true}/>
      </HelpHolder>}
    </ActionsContents>}
    {activeTab === "navigation" && (
      <ProgramTreeNavigation
        {...programTreeProps}
        isMobile={true}
        strings={strings}>
        <ProgramNumberSearch strings={strings}
          {...programSearchProps}/>
      </ProgramTreeNavigation>)}
  </MenuContainer>
}

export default MobileTopMenu;